<template>
  <div class="max-w-3xl mx-auto space-y-6 pb-16">
    <!-- Header -->
    <div class="text-center space-y-2">
      <h2 class="text-2xl font-bold text-slate-900 dark:text-white">
        🎉 {{ $t('AI_ONBOARDING.TEST.TITLE') || 'Test chatbot ngay bây giờ!' }}
      </h2>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('AI_ONBOARDING.TEST.SUBTITLE') || 'Chatbot đã sẵn sàng! Hãy thử trò chuyện' }}
      </p>
      <div class="flex items-center justify-center gap-2 text-xs sm:text-sm text-slate-500 dark:text-slate-300">
        <lucide-icon name="gauge" :size="14" />
        <span>
          <template v-if="isQuotaLoading">
            {{ $t('AI_ONBOARDING.TEST.REMAINING_LOADING') || 'Đang kiểm tra lượt test...' }}
          </template>
          <template v-else>
            {{ quotaLabel }}
          </template>
        </span>
      </div>
    </div>

    <!-- Chat Container -->
    <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <!-- Chat Header -->
      <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-4 flex items-center gap-3">
        <div class="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
          <lucide-icon name="bot" :size="24" class="text-white" />
        </div>
        <div class="flex-1">
          <h3 class="text-white font-semibold">{{ botName }}</h3>
          <p class="text-white/80 text-xs">{{ $t('AI_ONBOARDING.TEST.BOT_STATUS') || 'Đang online' }}</p>
        </div>
        <span class="px-3 py-1 bg-white/20 rounded-full text-white text-xs font-medium">
          {{ $t('AI_ONBOARDING.TEST.TEST_MODE') || 'TEST' }}
        </span>
      </div>

      <!-- Chat Messages -->
      <div class="relative p-4 sm:p-6 space-y-4 h-[360px] sm:h-[420px] md:h-[480px] max-h-[65vh] overflow-y-auto bg-slate-50 dark:bg-slate-900" ref="messagesContainer">
        <div v-for="(msg, idx) in messages" :key="idx" class="flex items-start gap-3" :class="msg.role === 'user' ? 'flex-row-reverse ml-auto' : ''">
          <div v-if="msg.role === 'bot'" class="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shrink-0">
            <lucide-icon name="bot" :size="16" class="text-white" />
          </div>
          
          <div v-if="msg.role === 'user'" class="w-8 h-8 rounded-full bg-slate-300 dark:bg-slate-600 flex items-center justify-center shrink-0">
            <lucide-icon name="user" :size="16" class="text-slate-600 dark:text-slate-300" />
          </div>
          
          <div class="max-w-[80%] sm:max-w-[70%] px-4 py-3 rounded-2xl text-sm leading-relaxed" :class="msg.role === 'user' ? 'bg-n-vars-right-bubble text-white' : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700'">
            <p class="text-sm whitespace-pre-wrap" v-html="msg.content"></p>
          </div>
        </div>

        <div v-if="isTyping" class="flex items-start gap-3">
          <div class="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
            <lucide-icon name="bot" :size="16" class="text-white" />
          </div>
          <div class="px-4 py-3 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <div class="flex gap-1">
              <div class="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
              <div class="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
              <div class="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Input -->
      <div class="p-4 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
        <div class="flex gap-2">
          <input
            v-model="inputMessage"
            type="text"
            :placeholder="$t('AI_ONBOARDING.TEST.PLACEHOLDER') || 'Nhập câu hỏi của bạn...'"
            class="flex-1 px-4 py-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur-sm focus:bg-slate-50 dark:focus:bg-slate-900 focus:outline-none text-sm"
            @keyup.enter="sendUserMessage"
            :disabled="isTyping"
          />
          <woot-button
            color-scheme="primary"
            :disabled="!inputMessage.trim() || isTyping"
            @click="sendUserMessage"
            class="!bg-hilab hover:!bg-hilab-dark dark:!bg-hilab-dark dark:hover:!bg-hilab"
          >
            <lucide-icon name="send" :size="18" />
          </woot-button>
        </div>
        <p v-if="quotaDescription" class="text-xs text-slate-500 dark:text-slate-400 mt-2 flex items-center gap-1">
          <lucide-icon name="info" :size="12" />
          {{ quotaDescription }}
        </p>
      </div>
    </div>

    <!-- Feedback -->
    <div class="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 space-y-3">
      <h4 class="font-semibold text-slate-900 dark:text-white text-center">
        {{ $t('AI_ONBOARDING.TEST.FEEDBACK_TITLE') || 'Chatbot hoạt động thế nào?' }}
      </h4>
      <div class="flex flex-wrap justify-center gap-2">
        <button
          v-for="opt in feedbackOptions"
          :key="opt.value"
          class="flex flex-col items-center gap-1 px-4 py-2 border-2 rounded-lg transition-all"
          :class="feedback === opt.value 
            ? 'border-hichat-500 bg-hichat-50 dark:bg-hichat-900/20' 
            : 'border-slate-200 dark:border-slate-700 hover:border-hichat-300'"
          @click="feedback = opt.value"
        >
          <span class="text-2xl">{{ opt.icon }}</span>
          <span class="text-xs font-medium text-slate-700 dark:text-slate-300">{{ opt.label }}</span>
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex';
import LucideIcon from 'dashboard/components/LucideIcon.vue';
import AiOnboardingAPI from 'dashboard/api/aiOnboarding';

export default {
  name: 'AiFirstChatTestStep',
  
  components: {
    LucideIcon,
  },

  emits: ['next', 'back'],

  data() {
    return {
      messages: [],
      inputMessage: '',
      isTyping: false,
      feedback: null,
      testQuota: {
        remaining: 5,
        limit: 5,
      },
      isQuotaLoading: true,
    };
  },

  computed: {
    ...mapState('aiOnboarding', {
      botProfile: state => state.botProfile,
      providerSetup: state => state.providerSetup,
      generated: state => state.generated,
    }),

    botName() {
      return this.botProfile.botName || this.$t('AI_ONBOARDING.TEST.DEFAULT_BOT_NAME') || 'AI Bot';
    },

    feedbackOptions() {
      return [
        { value: 'excellent', icon: '🤩', label: this.$t('AI_ONBOARDING.TEST.FEEDBACK_OPTIONS.EXCELLENT') || 'Tuyệt vời' },
        { value: 'good', icon: '😊', label: this.$t('AI_ONBOARDING.TEST.FEEDBACK_OPTIONS.GOOD') || 'Tốt' },
        { value: 'okay', icon: '😐', label: this.$t('AI_ONBOARDING.TEST.FEEDBACK_OPTIONS.OKAY') || 'Bình thường' },
        { value: 'needs_work', icon: '😕', label: this.$t('AI_ONBOARDING.TEST.FEEDBACK_OPTIONS.NEEDS_WORK') || 'Cần cải thiện' },
      ];
    },

    quotaLabel() {
      const remaining = Math.max(this.testQuota.remaining, 0);
      const limit = this.testQuota.limit || 5;
      return (
        this.$t('AI_ONBOARDING.TEST.REMAINING', { remaining, limit }) ||
        `Còn ${remaining}/${limit} lượt test trong 1 giờ`
      );
    },

    quotaDescription() {
      const remaining = Math.max(this.testQuota.remaining, 0);
      const limit = this.testQuota.limit || 5;
      return (
        this.$t('AI_ONBOARDING.TEST.REMAINING_HINT', { remaining, limit }) ||
        `Bạn còn ${remaining}/${limit} lượt test. Giới hạn làm mới mỗi 60 phút.`
      );
    },
  },

  async mounted() {
    await this.loadTestQuota();
  },

  methods: {
    async loadTestQuota() {
      this.isQuotaLoading = true;
      try {
        const response = await AiOnboardingAPI.getTestChatQuota();
        if (response?.data?.success) {
          this.testQuota = {
            remaining: response.data.remaining,
            limit: response.data.limit,
          };
        }
      } catch (error) {
        console.error('Failed to fetch test chat quota:', error);
      } finally {
        this.isQuotaLoading = false;
      }
    },

    updateQuotaFromPayload(payload = {}) {
      if (typeof payload.test_limit === 'number') {
        this.testQuota.limit = payload.test_limit;
      }
      if (typeof payload.remaining_tests === 'number') {
        this.testQuota.remaining = Math.max(payload.remaining_tests, 0);
        return;
      }
      if (typeof payload.remaining === 'number') {
        this.testQuota.remaining = Math.max(payload.remaining, 0);
      } else {
        this.testQuota.remaining = Math.max(this.testQuota.remaining - 1, 0);
      }
    },

    async sendUserMessage() {
      const message = this.inputMessage.trim();
      if (!message || this.isTyping) return;

      this.messages.push({
        role: 'user',
        content: message,
        time: this.getCurrentTime(),
      });

      this.inputMessage = '';
      this.isTyping = true;
      this.$nextTick(() => this.scrollToBottom());

      try {
        const response = await AiOnboardingAPI.testChat({
          message,
          context: {
            bot_profile: this.botProfile,
            provider_setup: this.providerSetup,
            generated: this.generated,
          },
        });

        if (response.data && response.data.success) {
          this.messages.push({
            role: 'bot',
            content: response.data.message || this.$t('AI_ONBOARDING.TEST.NO_RESPONSE') || 'Xin lỗi, tôi không hiểu câu hỏi.',
            time: this.getCurrentTime(),
          });
          this.updateQuotaFromPayload(response.data);
        } else {
          throw new Error(response.data?.error || this.$t('AI_ONBOARDING.TEST.ERROR_MESSAGE') || 'Failed to get response');
        }

        this.$nextTick(() => this.scrollToBottom());
      } catch (error) {
        console.error('Test chat failed:', error);
        const errorMessage = error.response?.data?.message || this.$t('AI_ONBOARDING.TEST.ERROR_MESSAGE') || 'Xin lỗi, có lỗi xảy ra. Vui lòng thử lại.';
        this.messages.push({
          role: 'bot',
          content: errorMessage,
          time: this.getCurrentTime(),
        });
        if (error.response?.data) {
          this.updateQuotaFromPayload(error.response.data);
        }
      } finally {
        this.isTyping = false;
      }
    },

    getCurrentTime() {
      const now = new Date();
      return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
    },

    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
      }
    },

    handleNext() {
      if (this.feedback) {
        console.log('User feedback:', this.feedback);
      }
      this.$emit('next');
    },
  },
};
</script>
